// Central source of truth for subscription tiers.
// Used by packages/api (enforcement), packages/app (UI gating), packages/web (pricing page).
//
// NOTE: The tier system is kept for feature gating only.
// User limits are managed via organisations.user_limit column in the DB.
export const TIER_HIERARCHY = ['free', 'starter', 'business', 'enterprise', 'physical'];
// Features that require a minimum tier
export const FEATURE_REQUIREMENTS = {
    bulk_import: 'starter',
    apple_wallet: 'starter',
    // Analytics and the lead CRM (incl. business-card scan) are Business features
    analytics: 'business',
    analytics_30d: 'business',
    leads: 'business',
    tracking_links: 'business',
    analytics_90d: 'business',
    analytics_unlimited: 'enterprise',
    audit_log_7d: 'starter',
    audit_log_90d: 'business',
    audit_log_365d: 'enterprise',
    backup_json: 'starter',
    backup_full: 'business',
    custom_smtp: 'business',
    departments: 'business',
    custom_domain: 'enterprise',
    app_theme_overrides: 'business',
    api_read: 'business',
    api_full: 'enterprise',
    sso: 'enterprise',
    white_label: 'enterprise',
};
// NOTE: Card limits are removed — cards per user are unlimited.
// User slots are purchased via Stripe (organisations.user_limit).
export const TIER_LIMITS = {
    free: {
        users: 1,
        customDomains: 0,
        linkPages: 1,
    },
    // Users are bought as seats (organisations.seats_*), not limited by the plan
    starter: {
        users: null,
        customDomains: 0,
        linkPages: 3,
    },
    business: {
        users: null,
        customDomains: 0,
        linkPages: 10,
    },
    enterprise: {
        users: null,
        customDomains: null,
        linkPages: 50,
    },
    physical: {
        users: 1,
        customDomains: 0,
        linkPages: 1,
    },
};
export const TIER_PRICING = {
    free: { monthly: 0, yearly: 0, label: 'Free' },
    starter: { monthly: 300, yearly: 1299, label: 'Starter' },
    business: { monthly: 800, yearly: 8500, label: 'Business' },
    enterprise: { monthly: null, yearly: null, label: 'Enterprise' },
    physical: { monthly: null, yearly: null, label: 'Physische Karte', oneTime: 2999 },
};
/** Length of the free test period every new organisation gets. */
export const TRIAL_DAYS = 30;
/** Features of this tier are available during the free test period. */
export const TRIAL_TIER = 'starter';
export const TIER_THEMES = {
    free: ['vcard'],
    starter: ['vcard', 'minimal', 'mint'],
    business: ['vcard', 'minimal', 'mint'],
    enterprise: ['vcard', 'minimal', 'mint'],
    physical: ['vcard'],
};
/** Anything that is not a known tier (e.g. legacy 'individual') counts as 'free'. */
export function normalizeTier(value) {
    return typeof value === 'string' && TIER_HIERARCHY.includes(value) ? value : 'free';
}
/** The higher of two tiers (used to combine a purchased tier with an admin unlock). */
export function maxTier(a, b) {
    return TIER_HIERARCHY.indexOf(a) >= TIER_HIERARCHY.indexOf(b) ? a : b;
}
/** Feature a range needs; analytics as a whole is a Business feature. */
export function analyticsRangeFeature(range) {
    switch (range) {
        case '90d': return 'analytics_90d';
        case 'all': return 'analytics_unlimited';
        default: return 'analytics';
    }
}
export function tierAtLeast(current, required) {
    return TIER_HIERARCHY.indexOf(current) >= TIER_HIERARCHY.indexOf(required);
}
export function hasFeature(tier, feature) {
    const required = FEATURE_REQUIREMENTS[feature];
    if (!required)
        return true;
    return tierAtLeast(tier, required);
}
export function getLimits(tier) {
    return TIER_LIMITS[tier];
}
export function isAtLimit(tier, resource, currentCount) {
    const limit = TIER_LIMITS[tier][resource];
    if (limit === null)
        return false;
    return currentCount >= limit;
}
// Default user limit for new organisations (used by DB seed / admin UI)
export const DEFAULT_USER_LIMIT = 1;
//# sourceMappingURL=tiers.js.map